import { useEffect, useState, type ReactNode } from "react";
import {
  HomeframeSelect,
  Link,
  useRouterSnapshot,
  useHomeframeRouter,
} from "@builtbyted/homeframe";
export { useData } from "./data-cache";
export type Row = Record<string, unknown>;
export const num = (v: unknown) => Number(v || 0);
export const str = (v: unknown) => (v == null ? "" : String(v));
export const count = (v: unknown) => num(v).toLocaleString();
export const duration = (v: unknown) => {
  const n = Math.max(0, num(v));
  return n >= 3600
    ? `${Math.floor(n / 3600).toLocaleString()}h ${Math.floor((n % 3600) / 60)}m`
    : `${Math.floor(n / 60)}m`;
};
export const date = (v: unknown) =>
  num(v) ? new Date(num(v) * 1000).toLocaleString() : "—";
export const shortDate = (v: unknown) =>
  num(v) ? new Date(num(v) * 1000).toLocaleDateString() : "—";
export const timeAgo = (v: unknown) => {
  const n = Date.now() / 1000 - num(v);
  return !num(v)
    ? "Never"
    : n < 60
      ? "Just now"
      : n < 3600
        ? `${Math.floor(n / 60)}m ago`
        : n < 86400
          ? `${Math.floor(n / 3600)}h ago`
          : `${Math.floor(n / 86400)}d ago`;
};
export function Skeleton({
  kind = "rows",
  count = 4,
}: {
  kind?: "rows" | "chart" | "cards" | "posters" | "page";
  count?: number;
}) {
  return (
    <div
      className={`skeleton skeleton-${kind}`}
      role="status"
      aria-label="Loading"
    >
      {Array.from({ length: count }, (_, i) => (
        <div className="skeleton-item" key={i} aria-hidden="true">
          <i />
          <i />
          <i />
        </div>
      ))}
    </div>
  );
}
export function useQuery() {
  const route = useRouterSnapshot();
  return new URL(route.url, window.location.origin);
}
export function scoped(
  path: string,
  url: URL,
  extra: Record<string, string> = {},
) {
  const p = new URLSearchParams(
    Object.entries(extra).filter(([, v]) => v !== ""),
  );
  const server = url.searchParams.get("server");
  if (server) p.set("server_id", server);
  return `${path}?${p}`;
}
export function useLink(path: string) {
  const url = useQuery();
  return (
    path +
    (url.searchParams.get("server")
      ? `?server=${url.searchParams.get("server")}`
      : "")
  );
}

export function useNavigateQuery() {
  const router = useHomeframeRouter();
  return (values: Record<string, string>, pathname?: string) => {
    const url = new URL(window.location.href);
    if (pathname) url.pathname = pathname;
    for (const [k, v] of Object.entries(values)) {
      if (v) url.searchParams.set(k, v);
      else url.searchParams.delete(k);
    }
    void router.navigate(url.pathname + url.search);
  };
}
export function Page({
  title,
  tools,
  children,
}: {
  title: string;
  tools?: ReactNode;
  children: ReactNode;
}) {
  return (
    <main className="page">
      <div className="page-title">
        <h1>{title}</h1>
        {tools}
      </div>
      {children}
    </main>
  );
}
export function Message({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={error ? "message error" : "message"}
      role={error ? "alert" : "status"}
    >
      {children}
    </div>
  );
}
export function Section({
  title,
  tools,
  children,
}: {
  title: string;
  tools?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="section">
      <div className="padded-header">
        <h2>{title}</h2>
        {tools}
      </div>
      {children}
    </section>
  );
}
export function Poster({
  server,
  path,
  title = "",
  square = false,
}: {
  server: unknown;
  path: unknown;
  title?: string;
  square?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const p = str(path);
  useEffect(() => setFailed(false), [p]);
  return (
    <div className={`poster${square ? " square" : ""}`}>
      {p && !failed ? (
        <img
          src={`/api/image/${server}?path=${encodeURIComponent(p)}&width=300`}
          alt={title}
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        <span aria-hidden="true">▶</span>
      )}
    </div>
  );
}
export function DayPicker() {
  const url = useQuery();
  const navigateQuery = useNavigateQuery();
  return (
    <label className="inline-control">
      Last{" "}
      <HomeframeSelect
        aria-label="Statistics period"
        value={url.searchParams.get("days") || "30"}
        onChange={(e) => navigateQuery({ days: e.target.value })}
      >
        {[7, 30, 90, 365, 36500].map((v) => (
          <option key={v} value={v}>
            {v === 36500 ? "All time" : `${v} days`}
          </option>
        ))}
      </HomeframeSelect>
    </label>
  );
}
export function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}
export function TopList({
  title,
  rows,
  posters = false,
}: {
  title: string;
  rows?: Row[] | undefined;
  posters?: boolean;
}) {
  const url = useQuery();
  return (
    <article className="stats-card">
      <h3>{title}</h3>
      {!rows && <Skeleton />}
      {rows?.slice(0, 5).map((r, i) => {
        const content = (
          <>
            <span className="rank">{i + 1}</span>
            {posters && <Poster server={r.server_id} path={r.thumb} />}
            <span className="ranking-label">
              {str(r.label) || "Unknown"}
              <small>
                {r.year ? `${str(r.year)} · ` : ""}
                {duration(r.duration)}
              </small>
            </span>
            <strong>
              {count(r.plays)}
              <small>plays</small>
            </strong>
          </>
        );
        const query = new URLSearchParams({
          title: str(r.label),
          type: str(r.media_type),
          days: url.searchParams.get("days") || "30",
        });
        if (r.year) query.set("year", str(r.year));
        const server = url.searchParams.get("server");
        if (server) query.set("server", server);
        return r.media_type && r.label ? (
          <Link
            key={i}
            className="ranking ranking-link"
            to={`/media?${query}`}
            aria-label={`${str(r.label)} viewing details`}
          >
            {content}
            <span aria-hidden="true">›</span>
          </Link>
        ) : (
          <div className="ranking" key={i}>
            {content}
          </div>
        );
      })}
      {rows?.length === 0 && <p className="subtle">No plays in this period.</p>}
    </article>
  );
}
